/**
 * What the sign-off screens say and which buttons they offer.
 *
 * The rules live on the server (backend/utils/draftSignoff.js) and it has the
 * last word: a button shown here that the server refuses just produces its
 * message. This only decides what is worth OFFERING, so the screen is not a
 * wall of buttons that mostly fail.
 */

export const CHECKER_REQUIRED = ['resolution', 'minutes', 'report'];

export const needsChecker = (documentType) => CHECKER_REQUIRED.includes(documentType);

const STATUS = {
  draft: { label: 'Draft', bg: '#edeef3', color: '#3a4a66', hint: 'Not yet sent for review.' },
  in_review: { label: 'In review', bg: '#f3ebd7', color: '#8a6a1f', hint: 'Waiting for a reviewer to approve it or ask for changes.' },
  changes_requested: { label: 'Changes requested', bg: '#f4e4e0', color: '#9a564a', hint: 'The reviewer asked for changes. Edit it and send it again.' },
  approved: { label: 'Approved', bg: '#e8efe5', color: '#4a6b3f', hint: 'Checked. A professional can now sign it.' },
  signed: { label: 'Signed', bg: '#e1ece9', color: '#145f5f', hint: 'Signed by a professional. Any edit to the text voids the signature.' },
};

/** Chip text and colours for a status; a draft with no sign-off yet reads as Draft. */
export const statusView = (signoff) => STATUS[(signoff && signoff.status) || 'draft'] || STATUS.draft;

/**
 * The actions worth offering this viewer right now.
 *
 * @param {object} p
 * @param {object} [p.signoff]
 * @param {string} p.documentType
 * @param {boolean} p.isProfessional
 * @param {boolean} p.canEdit
 * @returns {Array<{ action: string, label: string, primary?: boolean, needsNote?: boolean }>}
 */
export function actionsFor({ signoff, documentType, isProfessional, canEdit }) {
  const status = (signoff && signoff.status) || 'draft';
  const out = [];
  if ((status === 'draft' || status === 'changes_requested') && canEdit) {
    out.push({ action: 'submit', label: 'Send for review', primary: true });
  }
  if (status === 'in_review') {
    out.push({ action: 'approve', label: 'Approve', primary: true });
    out.push({ action: 'request_changes', label: 'Ask for changes', needsNote: true });
  }
  if (isProfessional) {
    if (status === 'approved') out.push({ action: 'sign', label: 'Sign', primary: true });
    // A plain document needs no checker: a professional may sign it straight from draft.
    if (status === 'draft' && !needsChecker(documentType)) out.push({ action: 'sign', label: 'Sign without review' });
  }
  return out;
}

/** One line per thing that happened, newest last, for the trail on the dialog. */
const VERB = {
  submit: 'sent for review',
  approve: 'approved',
  request_changes: 'asked for changes',
  sign: 'signed',
  reset_by_edit: 'text edited, sign-off reset',
};

export function historyLines(signoff) {
  return ((signoff && signoff.history) || []).map((h) => {
    const who = h.byName || 'Someone';
    const self = h.selfApproved ? ' (their own draft, no second reviewer)' : '';
    const note = h.note ? `: ${h.note}` : '';
    return { at: h.at, text: `${who} ${VERB[h.action] || h.action}${self}${note}` };
  });
}
